import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { Navigate, useNavigate } from 'react-router-dom'

import { BEIGE_200 } from 'constants/styling/theme'
import { Path } from 'constants/router'
import { areaRangeOf, segmentForSurface, sizeGateReason } from 'utils/purchaseFlow/segmentForSurface'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { embedSession } from 'utils/embedSession'
import { logger } from 'utils/logging'
import { EmbedPropertyError } from 'components/common/Embed/EmbedPropertyError/EmbedPropertyError'
import type { PropertySurfaceDTO } from 'models/property'
import type { PropertyCompletionResult } from './PropertyCompletionForm.component'
import { PropertySelectionDrawer } from './PropertySelectionDrawer.component'
import { propertyRefFromDetail, useEmbedPresetProperty } from './useEmbedPresetProperty'
import { PfRevampEventName, type PropertySizeSource } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'
import { announceFlowLoaded } from '../routing/flowLoadedGate'
import { PfStepPath, usePfPath } from '../routing/pfBasePath'
import { claimSizeAnnouncement } from '../routing/sizeAnnouncementGate'
import { usePfEmbed } from '../routing/usePfEmbed'
import { usePfStore } from '../store'

/** Whether a property's band comes from its exact surface or from a picked area range, as the backend reads it. */
const sizeSourceOf = (property: { surface?: PropertySurfaceDTO, areaRange?: string }): PropertySizeSource | null => {
  if (segmentForSurface(property.surface)) return 'exact_area'
  return property.areaRange ? 'range' : null
}

/**
 * Variant A entry page — property selection first.
 *
 * Reached by direct-link, by the mobile menu entry action, and by the
 * `/select-user` legacy redirect. The TopBar split-button opens the same drawer
 * as an overlay on the current page (dogfood-2 finding 2), so this dedicated
 * page is mostly a fallback. On X-out, redirect to home so users don't get
 * stranded on a blank canvas.
 *
 * Admins see the drawer's client-selection stage first — that logic lives in
 * the drawer, so this page needs no role handling.
 *
 * Inside the Propstack embed the CRM has already chosen the property
 * (`?objectReferenceId`), so — as in legacy — it is selected for the client and
 * the step is skipped. The drawer only appears when that property still needs
 * completing, locked to it, and nothing on this screen may leave the iframe.
 */
export const PropertyFirstEntry: React.FC = () => {
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(true)

  const { isEmbed, propertyRef } = usePfEmbed()
  const pfPath = usePfPath()
  const preset = useEmbedPresetProperty(propertyRef)

  // The usual first screen of a visit, and therefore the usual denominator of the funnel. Never on the
  // spinner, and not when the complete property skips ahead: the catalogue step announces itself then.
  const presetSkipsAhead = preset.status === 'ready' && preset.isComplete
  useEffect(() => {
    if (preset.status === 'loading' || presetSkipsAhead) return
    if (preset.status === 'error') announceFlowLoaded('select_property', 'PROPERTY_FETCH_FAILED')
    else announceFlowLoaded('select_property')
  }, [preset.status, presetSkipsAhead])
  const storedPropertyId = usePfStore((s) => s.property?.id)
  const setProperty = usePfStore((s) => s.setProperty)
  const presetReady = preset.status === 'ready' && preset.isComplete ? preset.property : null

  useEffect(() => {
    if (!presetReady || storedPropertyId === presetReady.id) return
    setProperty(propertyRefFromDetail(presetReady))
  }, [presetReady, storedPropertyId, setProperty])

  // The embed order's size, once per flow like legacy's area step: the band of the CRM property's surface or the
  // range picked for it, and a property with neither is the size gate, which the completion form closes.
  const presetProperty = preset.status === 'ready' ? preset.property : null
  const isPresetComplete = preset.status === 'ready' && preset.isComplete
  useEffect(() => {
    if (!presetProperty || !claimSizeAnnouncement()) return
    const segment = areaRangeOf(presetProperty)
    if (!segment) {
      dispatchPfRevampEvent({ type: PfRevampEventName.SIZE_GATE_BLOCKED, payload: { reason: sizeGateReason(presetProperty.surface) } })
    } else if (isPresetComplete) {
      dispatchPfRevampEvent({ type: PfRevampEventName.PROPERTY_SIZE_SELECTED, payload: { segment, sizePrefilled: true, gateShown: false, sizeSource: sizeSourceOf(presetProperty) } })
    }
  }, [presetProperty, isPresetComplete])

  const handleCompletionSaved = useCallback(({ areaRange }: PropertyCompletionResult) => {
    if (!presetProperty) return
    const entered = !!areaRange
    dispatchPfRevampEvent({
      type: PfRevampEventName.PROPERTY_SIZE_SELECTED,
      payload: {
        segment: entered ? areaRangeOf({ areaRange }) : areaRangeOf(presetProperty),
        sizePrefilled: !entered,
        gateShown: entered,
        sizeSource: entered ? 'range' : sizeSourceOf(presetProperty),
      },
    })
  }, [presetProperty])

  // A dead end for the broker: the CRM's property is not in the workspace. Legacy logs the same screen.
  const presetErrorStatus = preset.status === 'error' ? preset.httpStatus ?? 'none' : null
  useEffect(() => {
    if (presetErrorStatus === null) return
    logger.warn('Embed property could not be resolved', { status: presetErrorStatus }, {
      context: {
        component: 'PropertyFirstEntry',
        action: 'embedPropertyUnresolved',
        metadata: { status: presetErrorStatus, crm: embedSession.getProvider() ?? 'none' },
      },
    })
  }, [presetErrorStatus])

  const handleClose = useCallback(() => {
    // Outside the flow is the main app, which the iframe must never become.
    if (isEmbed) return
    setDrawerOpen(false)
    navigate(Path.INDEX)
  }, [isEmbed, navigate])

  if (preset.status === 'loading') return <LoadingPage />
  if (preset.status === 'error') return <EmbedPropertyError />
  if (presetReady && storedPropertyId === presetReady.id) return <Navigate to={pfPath(PfStepPath.CATALOGUE)} replace />
  if (presetReady) return <LoadingPage />

  return (
    <Box minHeight="100vh" bgcolor={BEIGE_200} data-cy="pf-revamp-property-first-entry">
      <PropertySelectionDrawer
        isOpen={drawerOpen}
        onClose={handleClose}
        lockedProperty={presetProperty ?? undefined}
        closable={!isEmbed}
        showDraftOrders={!isEmbed}
        onCompletionSaved={handleCompletionSaved}
      />
    </Box>
  )
}
